import {
  AGENDA_CATEGORY_COLORS,
  NON_BUSINESS_DAY_POLICIES,
  ROUTINE_KINDS,
  ROUTINE_WEEKS_OF_MONTH,
  type AgendaCategoryColor,
  type NonBusinessDayPolicy,
  type RoutineCadence,
  type RoutineKind,
} from './enums.js'
import type { Occurrence, Routine } from './domain-extra.js'

/**
 * Regras de rotina: rótulos, cadência e cálculo da próxima ocorrência.
 *
 * Mora nos contratos, e não na tela, porque três consumidores precisam da
 * mesma resposta: a página de Rotinas, a Agenda e — quando existir — o worker
 * que materializa ocorrência (SDD 09 R1). Duas implementações da mesma conta
 * dariam duas datas diferentes para a mesma rotina.
 *
 * Tudo aqui é puro e trabalha em data local `yyyy-MM-dd`. Nada de `Date` que
 * atravesse fuso: o dia da reunião é um dia de calendário, não um instante.
 */

export const ROUTINE_KIND_LABEL: Record<RoutineKind, string> = {
  alinhamento_semanal: 'Alinhamento semanal',
  objetivos_mensais: 'Objetivos mensais',
  prova_mensal: 'Prova mensal',
  coleta_feedback: 'Coleta de feedback',
  entrega_feedback: 'Entrega de feedback',
  um_a_um: '1:1',
  revisao_processos: 'Revisão de processos',
  outro: 'Outro',
}

export const NON_BUSINESS_DAY_POLICY_LABEL: Record<NonBusinessDayPolicy, string> = {
  proximo: 'Adia para o próximo dia útil',
  anterior: 'Antecipa para o dia útil anterior',
  pular: 'Pula a ocorrência',
}

export const AGENDA_CATEGORY_COLOR_LABEL: Record<AgendaCategoryColor, string> = {
  indigo: 'Índigo',
  violet: 'Violeta',
  blue: 'Azul',
  cyan: 'Ciano',
  teal: 'Verde-azulado',
  green: 'Verde',
  lime: 'Lima',
  amber: 'Âmbar',
  orange: 'Laranja',
  red: 'Vermelho',
  pink: 'Rosa',
  slate: 'Chumbo',
}

/** Domingo = 0, como `Date.getDay()` e a coluna `weekday`. */
export const WEEKDAY_LABEL = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const

export const WEEKDAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const

/** Reexportado para o teste de cobertura de rótulos. */
export const ROUTINE_LABELLED_ENUMS = {
  ROUTINE_KINDS,
  NON_BUSINESS_DAY_POLICIES,
  AGENDA_CATEGORY_COLORS,
} as const

/**
 * Ordinais em pt-BR, com gênero.
 *
 * "primeira terça-feira" mas "primeiro sábado": domingo e sábado são
 * masculinos e os dias úteis são femininos (subentende-se "feira"). Concordar
 * errado num rótulo que o gestor lê toda semana é o tipo de detalhe que faz a
 * tela parecer feita por quem não fala a língua.
 */
const ORDINAL_FEMININO: Record<number, string> = {
  1: 'primeira',
  2: 'segunda',
  3: 'terceira',
  4: 'quarta',
  [-1]: 'última',
}
const ORDINAL_MASCULINO: Record<number, string> = {
  1: 'primeiro',
  2: 'segundo',
  3: 'terceiro',
  4: 'quarto',
  [-1]: 'último',
}

/** Domingo (0) e sábado (6) são masculinos; os dias de semana, femininos. */
export function ordinalLabel(week: number, weekday: number): string {
  const table = weekday === 0 || weekday === 6 ? ORDINAL_MASCULINO : ORDINAL_FEMININO
  return table[week] ?? `${week}ª`
}

type CadenceFields = Pick<Routine, 'weekday' | 'monthDay' | 'weekOfMonth'>

export function cadenceOf(routine: CadenceFields): RoutineCadence | null {
  if (routine.weekday !== undefined) {
    return routine.weekOfMonth !== undefined ? 'mensal_ordinal' : 'semanal'
  }
  if (routine.monthDay !== undefined) return 'mensal'
  return null
}

export function recurrenceLabel(routine: CadenceFields): string {
  if (routine.weekday !== undefined) {
    const day = WEEKDAY_LABEL[routine.weekday] ?? 'semana'
    if (routine.weekOfMonth !== undefined) {
      return `${ordinalLabel(routine.weekOfMonth, routine.weekday)} ${day} do mês`
    }
    return `toda ${day}`
  }
  if (routine.monthDay !== undefined) return `dia ${routine.monthDay} de cada mês`
  return 'sem recorrência'
}

// --- Datas -------------------------------------------------------------------

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function toIso(date: Date): string {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** `yyyy-MM-dd` → meio-dia local, para nenhum ajuste de fuso mudar o dia. */
function fromIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, 12)
}

function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

/** Datas `yyyy-MM-dd` de feriado e ponte (`sistemas.holiday`). */
export type HolidaySet = ReadonlySet<string>

const NO_HOLIDAYS: HolidaySet = new Set()

/** Fim de semana ou feriado — a mesma regra de `sistemas.is_business_day`. */
export function isOffDay(iso: string, holidays: HolidaySet = NO_HOLIDAYS): boolean {
  return isWeekend(fromIso(iso)) || holidays.has(iso)
}

/**
 * Aplica a política de dia não útil: fim de semana ou feriado do calendário
 * (`sistemas.holiday`). Sem o conjunto de feriados, só fim de semana.
 *
 * `pular` devolve `null`: a ocorrência daquele ciclo não acontece. A versão
 * anterior tratava `pular` como "mantém no sábado", que é o oposto.
 */
export function applyNonBusinessDayPolicy(
  iso: string,
  policy: NonBusinessDayPolicy,
  holidays: HolidaySet = NO_HOLIDAYS,
): string | null {
  if (!isOffDay(iso, holidays)) return iso
  if (policy === 'pular') return null

  const step = policy === 'proximo' ? 1 : -1
  const moved = fromIso(iso)
  do {
    moved.setDate(moved.getDate() + step)
  } while (isOffDay(toIso(moved), holidays))
  return toIso(moved)
}

/**
 * A n-ésima ocorrência de um dia da semana num mês, em `yyyy-MM-dd`.
 *
 * `week = -1` é "a última", que não é sinônimo de "a quarta": em mês com cinco
 * terças, a última é a quinta. Tratar as duas como a mesma coisa erraria a
 * data em quatro a cinco meses por ano.
 *
 * Devolve `null` quando o ordinal não existe no mês (uma quinta segunda-feira,
 * por exemplo) — o ciclo simplesmente não acontece, como manda a semântica de
 * "a quinta segunda do mês".
 */
export function ordinalWeekdayOf(
  year: number,
  monthIndex: number,
  weekday: number,
  week: number,
): string | null {
  if (week === -1) {
    const last = new Date(year, monthIndex + 1, 0, 12)
    const back = (last.getDay() - weekday + 7) % 7
    last.setDate(last.getDate() - back)
    return toIso(last)
  }

  const first = new Date(year, monthIndex, 1, 12)
  const forward = (weekday - first.getDay() + 7) % 7
  const day = 1 + forward + (week - 1) * 7
  const lastDay = new Date(year, monthIndex + 1, 0).getDate()
  if (day > lastDay) return null
  return toIso(new Date(year, monthIndex, day, 12))
}

/**
 * Próxima data da rotina a partir de `fromIso` (inclusive).
 *
 * Devolve `null` quando a rotina não tem cadência ou quando a política é
 * `pular` e os próximos ciclos caem todos em fim de semana dentro do horizonte
 * consultado.
 *
 * Três furos da versão que vivia na tela, todos cobertos por teste:
 * - dia 31 em mês de 30 dias transbordava para o mês seguinte (`setDate(31)`
 *   em abril vira 1º de maio), então "dia 31" pulava meses inteiros;
 * - a comparação com "agora" usava hora, e no dia da rotina, depois do
 *   horário marcado, a data ainda aparecia como próxima;
 * - `pular` não pulava nada.
 */
export function nextOccurrenceOn(
  routine: CadenceFields & Pick<Routine, 'nonBusinessDayPolicy'>,
  from: string,
  options: { readonly horizonMonths?: number; readonly holidays?: HolidaySet } = {},
): string | null {
  const horizonMonths = options.horizonMonths ?? 12
  const holidays = options.holidays ?? NO_HOLIDAYS
  if (!ISO_DATE.test(from)) return null
  const cadence = cadenceOf(routine)
  if (cadence === null) return null

  const start = fromIso(from)

  if (cadence === 'mensal_ordinal') {
    const weekday = routine.weekday ?? 0
    const week = routine.weekOfMonth ?? 1
    for (let i = 0; i <= horizonMonths; i += 1) {
      const base = ordinalWeekdayOf(start.getFullYear(), start.getMonth() + i, weekday, week)
      if (base === null) continue
      const applied = applyNonBusinessDayPolicy(base, routine.nonBusinessDayPolicy, holidays)
      if (applied !== null && applied >= from) return applied
    }
    return null
  }

  if (cadence === 'semanal') {
    const weekday = routine.weekday ?? 0
    const delta = (weekday - start.getDay() + 7) % 7
    const candidate = new Date(start)
    candidate.setDate(start.getDate() + delta)
    // 53 semanas cobre o horizonte de um ano mesmo se toda ocorrência cair em
    // dia não útil com política `pular`.
    for (let i = 0; i < 53; i += 1) {
      const applied = applyNonBusinessDayPolicy(toIso(candidate), routine.nonBusinessDayPolicy, holidays)
      if (applied !== null && applied >= from) return applied
      candidate.setDate(candidate.getDate() + 7)
    }
    return null
  }

  const monthDay = routine.monthDay ?? 1
  for (let i = 0; i <= horizonMonths; i += 1) {
    const year = start.getFullYear()
    const month = start.getMonth() + i
    // Dia 0 do mês seguinte = último dia deste mês. "Dia 31" numa rotina
    // mensal significa "o último dia", não "1º do mês que vem".
    const lastDay = new Date(year, month + 1, 0).getDate()
    const day = Math.min(monthDay, lastDay)
    const applied = applyNonBusinessDayPolicy(
      toIso(new Date(year, month, day, 12)),
      routine.nonBusinessDayPolicy,
      holidays,
    )
    if (applied !== null && applied >= from) return applied
  }
  return null
}

/** `yyyy-MM-dd` + n dias, em data local. */
export function addDays(iso: string, days: number): string {
  const date = fromIso(iso)
  date.setDate(date.getDate() + days)
  return toIso(date)
}

/**
 * Todas as datas da rotina no intervalo `[from, to]`, inclusive.
 *
 * Existe para a Agenda parar de reimplementar a recorrência. A versão anterior
 * dela comparava `date.getDay() === weekday` direto na varredura do mês, o que
 * ignorava a política de dia não útil: a mesma rotina de sábado aparecia no
 * sábado no calendário e na segunda-feira na tela de Rotinas. Duas telas, duas
 * datas, o mesmo compromisso.
 *
 * `limit` é uma trava de segurança para o caso de alguém pedir um intervalo de
 * anos numa rotina semanal.
 */
export function occurrencesBetween(
  routine: CadenceFields & Pick<Routine, 'nonBusinessDayPolicy'>,
  from: string,
  to: string,
  options: { readonly limit?: number; readonly holidays?: HolidaySet } = {},
): string[] {
  const limit = options.limit ?? 400
  const holidays = options.holidays ?? NO_HOLIDAYS
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to) || from > to) return []

  const dates: string[] = []
  let cursor = from
  while (dates.length < limit) {
    const next = nextOccurrenceOn(routine, cursor, { holidays })
    if (next === null || next > to) break
    // A política pode empurrar duas ocorrências para o mesmo dia útil (duas
    // mensais que caem no sábado e no domingo do mesmo fim de semana, por
    // exemplo). O calendário mostra uma.
    if (dates.at(-1) !== next) dates.push(next)
    cursor = addDays(next, 1)
  }
  return dates
}

/**
 * Remarcação e cancelamento aplicados sobre a série gerada.
 *
 * A recorrência diz quando a rotina *deveria* acontecer; a ocorrência
 * registrada diz o que *aconteceu com aquela vaga*. Sem cruzar as duas, adiar
 * uma reunião não teria efeito nenhum no calendário — ela continuaria
 * desenhada no dia original, que é pior do que não deixar adiar.
 *
 * - vaga sem override → a data gerada;
 * - vaga com override `cancelada` → nada;
 * - vaga com override em outra data → a data efetiva.
 */
export interface OccurrenceOverride {
  readonly originalDate: string
  readonly date: string
  readonly status: Occurrence['status']
}

export interface ResolvedOccurrence {
  /** Onde desenhar no calendário. */
  readonly date: string
  /** A vaga da série — chave para abrir/editar a ocorrência. */
  readonly originalDate: string
  readonly status: Occurrence['status'] | 'projetada'
  /** Verdadeiro quando a data efetiva difere da vaga. */
  readonly rescheduled: boolean
}

export function resolveOccurrences(
  slots: readonly string[],
  overrides: readonly OccurrenceOverride[],
): ResolvedOccurrence[] {
  const bySlot = new Map(overrides.map((o) => [o.originalDate, o]))
  const resolved: ResolvedOccurrence[] = []

  for (const slot of slots) {
    const override = bySlot.get(slot)
    if (!override) {
      resolved.push({ date: slot, originalDate: slot, status: 'projetada', rescheduled: false })
      continue
    }
    if (override.status === 'cancelada') continue
    resolved.push({
      date: override.date,
      originalDate: slot,
      status: override.status,
      rescheduled: override.date !== slot,
    })
  }
  return resolved
}

// --- Validação ---------------------------------------------------------------

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/

export interface RoutineDraft {
  readonly name: string
  readonly kind: RoutineKind
  readonly cadence: RoutineCadence
  readonly weekday: number | null
  readonly monthDay: number | null
  readonly weekOfMonth: number | null
  readonly time: string
  readonly durationMinutes: number
  readonly ownerId: string
  readonly participantIds: readonly string[]
}

export type RoutineValidation = { ok: true } | { ok: false; field: string; reason: string }

/**
 * Espelha as constraints do banco antes do round-trip.
 *
 * Não substitui o banco — `routine_one_cadence` e os ranges continuam lá, e é
 * o banco que decide. Isto existe para a pessoa ver o erro no campo em vez de
 * receber um "violates check constraint" traduzido pela metade.
 */
export function validateRoutineDraft(draft: RoutineDraft): RoutineValidation {
  if (draft.name.trim().length < 3) {
    return { ok: false, field: 'name', reason: 'Dê um nome com pelo menos 3 caracteres.' }
  }
  if (draft.cadence === 'mensal') {
    if (draft.monthDay === null || draft.monthDay < 1 || draft.monthDay > 31) {
      return { ok: false, field: 'monthDay', reason: 'Escolha um dia entre 1 e 31.' }
    }
  } else {
    if (draft.weekday === null || draft.weekday < 0 || draft.weekday > 6) {
      return { ok: false, field: 'weekday', reason: 'Escolha o dia da semana.' }
    }
    if (draft.cadence === 'mensal_ordinal') {
      const week = draft.weekOfMonth
      if (week === null || !(ROUTINE_WEEKS_OF_MONTH as readonly number[]).includes(week)) {
        return {
          ok: false,
          field: 'weekOfMonth',
          reason: 'Escolha qual ocorrência do mês (primeira a quarta, ou a última).',
        }
      }
    }
  }
  if (!TIME_OF_DAY.test(draft.time)) {
    return { ok: false, field: 'time', reason: 'Informe o horário no formato HH:MM.' }
  }
  if (!Number.isInteger(draft.durationMinutes) || draft.durationMinutes < 5) {
    return { ok: false, field: 'durationMinutes', reason: 'A duração mínima é 5 minutos.' }
  }
  if (draft.durationMinutes > 8 * 60) {
    return { ok: false, field: 'durationMinutes', reason: 'A duração máxima é 8 horas.' }
  }
  if (draft.ownerId.trim().length === 0) {
    return { ok: false, field: 'ownerId', reason: 'Toda rotina precisa de um responsável.' }
  }
  return { ok: true }
}
